import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {Grid, Typography} from "@mui/material";
import {FormAutocomplete, FormTextField} from "../../../common/form-fields";
import {FormReportModel} from "../../../api/types/report";
import {monthsAtom} from "../../../state/reportRecoil";
import {useRecoilState} from "recoil";
import {SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import React, {useContext, useEffect, useState} from "react";
import {useLocationGroupRepository} from "../../../api/repositories/LocationGroupRepository";
import {fromGenericBeToGenericFe, setFormNumberValue} from "../../../utils";
import {numberIntegerRegex} from "../../../constants";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import DealerAndTenant, {DealerTenantEntity} from "../../../common/components/DealerAndTenant";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import dayjs from "dayjs";

const IncomingReservationsForm = ({readOnly}: { readOnly: boolean }) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //USE FORM CONTEXT
    const {register, setValue, watch} = useFormContext<FormReportModel>();

    //USE RECOIL ATOM
    const [monthsList] = useRecoilState(monthsAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState<
        SelectElementModelFE[]
    >(commercialSegmentListAtom);

    //USE STATE
    const [locationGroups, setLocationGroups] = useState<SelectElementModelFE[]>(
        []
    );
    const [dealerOptions, setDealerOptions] = useState([])
    const [yearList] = useState([dayjs().year() - 2, dayjs().year() - 1 ,dayjs().year(), dayjs().year() + 1, dayjs().year() + 2])

    //USE HOOK CUSTOM
    const {getLocationGroups} = useLocationGroupRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    const getStaticContent = async () => {
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    useEffect(() => {
        getStaticContent();
      if (optionsFiltered.length === 1) {
        updateDealerSelection(optionsFiltered[0]);
      } else {
        let legalEntity = optionsFiltered.find((el) => el.code === watch("legalEntity")?.code);
        let dealer = dealerOption.find((el) => el.code === watch("dealer")?.code);
        if (legalEntity) {
          updateDealerSelection(legalEntity);
        }
        if (dealer) {
          setValue("dealer", dealer);
        }
      }
    }, []);

    const updateDealerSelection = async (value: SelectElementModelFE) => {
        setValue(`legalEntity`, value);
        setValue(`dealer`, null);
        const filteredOptionsDealer = user.dealers?.filter(
            (el: DealerTenantEntity) => el.legalEntities?.some((el: { name: string | number; }) => el.name === value?.code)
        );
        const filteredList = filteredOptionsDealer.map(
            (el: DealerTenantEntity) => ({
                code: el.name,
                label: t('translation:dealers.'.concat(el.name)),
            })
        );
      await setDealerOptions(filteredList);
      if (filteredList.length === 1) {
        setValue(`dealer`, filteredList[0]);
      }

        const locationGroupsData: SelectElementModelBE[] =
            await getLocationGroups(value?.code as string);
        const locationsGroups: SelectElementModelFE[] =
            fromGenericBeToGenericFe(locationGroupsData);
        setLocationGroups([
            {code: 0, label: t("pricing:actions.selectAll")},
            ...locationsGroups,
        ]);
    };

  const renderDealerAndTenant = () => {
    return (
      <>
        <Typography variant="h6" width={"100%"} pl={2} mt={4}
                    hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length <= 1}>
          {t("pricing:alert.labels.dealerAndTenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length > 1}>
          {t("pricing:alert.labels.tenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={dealerFilteredOptions.length <= 1 || optionsFiltered.length > 1 }>
          {t("pricing:alert.labels.dealer")}
        </Typography>
        <Grid item xs={12} md={3} hidden={optionsFiltered.length <= 1}>
          <FormAutocomplete
            {...register("legalEntity")}
            label={`${t("pricing:dealer.tenant")}*`}
            //getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.label))}
            options={optionsFiltered || []}
            handleChange={updateDealerSelection}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
        <Grid item xs={12} md={3} hidden={dealerFilteredOptions.length <= 1}>
          <FormAutocomplete
            {...register(`dealer`)}
            label={`${t("pricing:dealer.dealer")}*`}
            options={dealerOptions || []}
            readOnly={Boolean(!watch("legalEntity"))}
            //getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.label))}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
      </>
    );
  };

    return (
        <Grid container columnSpacing={2}>
            {/* DEALER/TENANT */}
          {renderDealerAndTenant()}
            <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                {t("pricing:report.labels.searchParameters")}
            </Typography>
            {/* pastDays */}
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("incomingReservation.dayFromReservationDate")}
                    label={`${t("pricing:report.form.pastDays")}*`}
                    type={"text"}
                    handleChange={(value: string) =>
                        setFormNumberValue(
                            value,
                            setValue,
                            "incomingReservation.dayFromReservationDate",
                            numberIntegerRegex
                        )
                    }
                />
            </Grid>
            {/* M */}
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("incomingReservation.numberOfMonths")}
                    label={`${t("pricing:report.form.numberOfMonths")}*`}
                    type={"text"}
                    handleChange={(value: string) =>
                        setFormNumberValue(
                            value,
                            setValue,
                            "incomingReservation.numberOfMonths",
                            numberIntegerRegex
                        )
                    }
                />
            </Grid>
            {/* GROUPS OF LOCATIONS */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("incomingReservation.locationsGroupIds")}
                    label={`${t("pricing:report.labels.groupsOfLocations")}*`}
                    options={locationGroups}
                    isOptionEqualToValue={(
                        option: SelectElementModelFE,
                        value: SelectElementModelFE
                    ) => option.code === value.code}
                />
            </Grid>
            {/* MONTH PICK UP */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("incomingReservation.startMonthPickUp")}
                    label={`${t("pricing:report.form.startMonth")}*`}
                    options={monthsList}
                    isOptionEqualToValue={(option: string, value: string) =>
                        option === value
                    }
                />
            </Grid>
            {/* YEAR PICK UP */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("incomingReservation.year")}
                    label={`${t("pricing:report.form.year")}*`}
                    options={yearList}
                    isOptionEqualToValue={(option: number, value: number) =>
                        option === value
                    }
                />
            </Grid>
            {/* COMMERCIAL SEGMENT */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("incomingReservation.smCommercialSegmentId")}
                    label={`${t("pricing:report.form.smCommercialSegment")}`}
                    options={commercialSegmentList}
                    isOptionEqualToValue={(
                        option: SelectElementModelFE,
                        value: SelectElementModelFE
                    ) => option.code === value.code}
                />
            </Grid>
        </Grid>
    );
};

export default IncomingReservationsForm;
